import React, { useCallback, useEffect, useState } from 'react';
import {
  getDashboardOverviewApi,
  getDashboardMetricsApi,
  getOnboardingFunnelApi,
  getStateDistributionApi,
  getPriorityAlertsApi,
  getRecentCampaignsApi,
} from '../api/dashboard.api';
import { getRealTimeMetricsApi } from '../api/analytics.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';
import DashboardKpiStrip from '../components/dashboard/DashboardKpiStrip';
import DashboardTodayStrip from '../components/dashboard/DashboardTodayStrip';
import DashboardQuickActions from '../components/dashboard/DashboardQuickActions';
import DashboardRecentCampaigns from '../components/dashboard/DashboardRecentCampaigns';
import DashboardPriorityAlerts from '../components/dashboard/DashboardPriorityAlerts';
import DashboardFunnelChart from '../components/dashboard/DashboardFunnelChart';
import DashboardStateChart from '../components/dashboard/DashboardStateChart';
import DashboardCircleHealth from '../components/dashboard/DashboardCircleHealth';
import DashboardBillingOverview from '../components/dashboard/DashboardBillingOverview';
import '../assets/css/dashboard.css';

const EMPTY_OVERVIEW = {
  metrics: {
    activeUsers: 0,
    freemiumUsers: 0,
    paidUsers: 0,
    trustedCircles: 0,
    pendingInvites: 0,
    organizations: 0,
    segments: 0,
    campaignsNeedingAction: 0,
  },
  today: { newUsersToday: 0, alertsToday: 0, campaignsToday: 0 },
  trends: { newUsers7d: 0, newUsersPrev7d: 0, activeUsersDelta: 0 },
  funnel: [],
  states: [],
  priorityAlerts: [],
  recentCampaigns: [],
  circleHealth: { complete: 0, incomplete: 0, pendingInvites: 0, avgSize: null },
};

async function loadLegacyOverview() {
  const [m, f, s, p, c, rt] = await Promise.all([
    apiHandler(() => getDashboardMetricsApi()),
    apiHandler(() => getOnboardingFunnelApi()),
    apiHandler(() => getStateDistributionApi()),
    apiHandler(() => getPriorityAlertsApi()),
    apiHandler(() => getRecentCampaignsApi({ limit: 5 })),
    apiHandler(() => getRealTimeMetricsApi()),
  ]);
  const metrics = m.success ? unwrapData(m) : EMPTY_OVERVIEW.metrics;
  const realtime = rt.success ? unwrapData(rt) : null;
  const pendingCampaigns = (p.success ? unwrapData(p) : []).find(
    (a) => a.alertKey === 'pending_campaigns'
  );
  return {
    ...EMPTY_OVERVIEW,
    metrics: {
      ...EMPTY_OVERVIEW.metrics,
      ...metrics,
      organizations: metrics.organizations ?? 0,
      segments: metrics.segments ?? 0,
      campaignsNeedingAction: pendingCampaigns?.count ?? 0,
    },
    today: realtime
      ? {
          newUsersToday: realtime.newUsersToday ?? 0,
          alertsToday: realtime.alertsToday ?? 0,
          campaignsToday: realtime.campaignsToday ?? 0,
        }
      : EMPTY_OVERVIEW.today,
    circleHealth: {
      complete: metrics.trustedCircles ?? 0,
      incomplete:
        (p.success ? unwrapData(p) : []).find((a) => a.alertKey === 'incomplete_circles')?.count ?? 0,
      pendingInvites: metrics.pendingInvites ?? 0,
      avgSize: null,
    },
    funnel: f.success ? unwrapData(f) || [] : [],
    states: s.success ? unwrapData(s) || [] : [],
    priorityAlerts: p.success ? unwrapData(p) || [] : [],
    recentCampaigns: c.success ? unwrapData(c)?.campaigns || [] : [],
  };
}

export default function Dashboard() {
  const [dateLabel, setDateLabel] = useState('');
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await apiHandler(() => getDashboardOverviewApi({ limit: 5 }));
    if (result.success) {
      const data = unwrapData(result);
      setOverview({
        ...EMPTY_OVERVIEW,
        ...data,
        metrics: { ...EMPTY_OVERVIEW.metrics, ...data?.metrics },
        today: { ...EMPTY_OVERVIEW.today, ...data?.today },
        trends: { ...EMPTY_OVERVIEW.trends, ...data?.trends },
        circleHealth: { ...EMPTY_OVERVIEW.circleHealth, ...data?.circleHealth },
      });
    } else {
      const legacy = await loadLegacyOverview();
      setOverview(legacy);
      setError(result.message || 'Overview unavailable — showing partial data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setDateLabel(
      new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    );
    loadDashboard();
    const interval = setInterval(loadDashboard, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const { metrics, today, trends, funnel, states, priorityAlerts, recentCampaigns, circleHealth } =
    overview;

  return (
    <>
      <PageHeader
        title="Dashboard"
        icon="fa-gauge-high"
        hideBreadcrumb
        actions={
          <>
            <span style={{ fontSize: 12, color: '#64748B', alignSelf: 'center' }}>{dateLabel}</span>
            <button
              type="button"
              className="action-btn btn-orange"
              onClick={loadDashboard}
              disabled={loading}
            >
              <i className={`fas fa-rotate${loading ? ' fa-spin' : ''}`} /> Refresh
            </button>
          </>
        }
      />

      <div className="db-page fade-up">
        {error ? (
          <div className="db-error-banner" role="alert">
            <span>{error}</span>
            <button type="button" onClick={loadDashboard}>
              Retry
            </button>
          </div>
        ) : null}

        <DashboardTodayStrip today={today} loading={loading} />

        <DashboardKpiStrip metrics={metrics} today={today} trends={trends} loading={loading} />

        <DashboardQuickActions />

        <div className="db-main-grid">
          <DashboardRecentCampaigns campaigns={recentCampaigns} loading={loading} />
          <DashboardPriorityAlerts alerts={priorityAlerts} loading={loading} />
        </div>

        <div className="db-charts-grid">
          <DashboardFunnelChart funnel={funnel} loading={loading} />
          <DashboardStateChart states={states} loading={loading} />
        </div>

        <div className="db-charts-grid mt-4">
          <DashboardCircleHealth circleHealth={circleHealth} loading={loading} />
          <DashboardBillingOverview loading={loading} />
        </div>
      </div>
    </>
  );
}
